import { queryOptions, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Info } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Card } from "@/components/ui";
import { ImportPanel } from "@/features/import/ImportPanel";
import { uomDimensions, uomKeys, uomListQuery, type Uom } from "@/features/items/api";
import { useFormat } from "@/lib/format";
import { useCan } from "@/lib/session";
import { isDemoMode, supabase } from "@/lib/supabase";
import { TableToolbar } from "@/components/TableToolbar";
import { useTableView, type TableColumn } from "@/components/TableView";
import type { ExportColumn } from "@/lib/exportXlsx";

// Units of measure and standard (global) conversions. A tenant has a few
// dozen units at most, so this is a plain table: one request each, both
// cached for 10 minutes. Units are added and maintained by import (ADR-034);
// the defaults are seeded for every new organization.

interface Search {
  import?: "units" | undefined;
}

interface GlobalConversion {
  id: string;
  factor: string;
  from_uom: { code: string } | null;
  to_uom: { code: string } | null;
}

const globalConversionsQuery = queryOptions({
  queryKey: ["master-data", "uom-conversions", "global"] as const,
  queryFn: async ({ signal }): Promise<GlobalConversion[]> => {
    if (isDemoMode || !supabase) {
      return [
        ["KG", "G", "1000"],
        ["T", "KG", "1000"],
        ["M", "MM", "1000"],
        ["L", "ML", "1000"],
      ].map(([from, to, factor]) => ({ id: `${from}-${to}`, factor: factor!, from_uom: { code: from! }, to_uom: { code: to! } }));
    }
    const { data, error } = await supabase
      .from("uom_conversions")
      .select("id,factor,from_uom:uoms!uom_conversions_from_fkey(code),to_uom:uoms!uom_conversions_to_fkey(code)")
      .is("item_id", null)
      .abortSignal(signal);
    if (error) throw error;
    return data as unknown as GlobalConversion[];
  },
  staleTime: 10 * 60_000,
});

export const Route = createFileRoute("/_app/master-data/units")({
  validateSearch: (raw: Record<string, unknown>): Search => (raw.import === "units" ? { import: "units" } : {}),
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(uomListQuery),
      context.queryClient.ensureQueryData(globalConversionsQuery),
    ]),
  component: UnitsPage,
});

function UnitsPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const uomList = useQuery(uomListQuery);
  const allUoms = useMemo(() => uomList.data ?? [], [uomList.data]);
  // Units are a small dictionary loaded whole: column filters and sort in the browser.
  const unitColumns = useMemo<TableColumn<Uom>[]>(
    () => [
      { key: "code", header: t("units.colCode"), type: "text", value: (u) => u.code },
      { key: "name", header: t("units.colName"), type: "text", value: (u) => u.name },
      {
        key: "dimension",
        header: t("units.colDimension"),
        type: "enum",
        value: (u) => u.dimension,
        options: uomDimensions.map((d) => ({ value: d, label: t(`dimension.${d}`) })),
      },
      { key: "decimals", header: t("units.colDecimals"), type: "number", value: (u) => u.decimals, align: "end" },
      { key: "active", header: t("units.colStatus"), type: "bool", value: (u) => u.is_active },
    ],
    [t],
  );
  const table = useTableView(allUoms, unitColumns);
  const uoms = table.rows;
  const { data: conversions = [] } = useQuery(globalConversionsQuery);
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const mayImport = useCan("core.data.import");
  const mayManage = useCan("master.uom.manage");
  const canImport = mayImport && mayManage;
  // Same codes as the import template (re-importable, ADR-022).
  const exportColumns: ExportColumn<Uom>[] = [
    { header: "code", kind: "code", value: (u) => u.code },
    { header: "name", kind: "text", value: (u) => u.name },
    { header: "dimension", kind: "code", value: (u) => u.dimension },
    { header: "decimals", kind: "integer", value: (u) => u.decimals, sum: false },
    { header: "is_active", kind: "boolean", value: (u) => u.is_active },
  ];

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.masterData.title")} <span aria-hidden="true">/</span> {t("nav.masterData.units")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("units.title")}</h1>
        </div>
      </header>

      <p className="flex items-center gap-2 self-start rounded-lg bg-surface-muted px-3 py-1.5 text-[13px] text-ink-muted">
        <Info size={14} aria-hidden="true" />
        {t("units.note")}
      </p>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Card aria-label={t("units.listLabel")} className="overflow-hidden">
          <TableToolbar
            label={t("units.listLabel")}
            tools={{
              id: "units",
              title: t("units.title"),
              onImport: canImport ? () => void navigate({ search: { import: "units" }, replace: true }) : undefined,
            }}
            exportColumns={exportColumns}
            rows={uoms}
            onRefresh={() => void uomList.refetch()}
            refreshing={uomList.isRefetching}
            hasView={table.hasView}
            onClearView={table.clear}
          />
          {table.chips && <div className="border-b border-border-subtle px-3 py-2">{table.chips}</div>}
          {table.popover}
          <div className="overflow-x-auto">
            <table className="tabular w-full border-collapse text-[13px]" aria-label={t("units.listLabel")}>
              <thead>
                <tr className="bg-surface-muted text-left text-ink-muted">
                  {table.th("code")}
                  {table.th("name")}
                  {table.th("dimension")}
                  {table.th("decimals", "text-right")}
                  {table.th("active")}
                </tr>
              </thead>
              <tbody>
                {uoms.map((u) => (
                  <tr key={u.id} className="border-t border-border-subtle" style={{ height: "var(--row-h)" }}>
                    <td className="px-4 font-mono text-xs font-medium">{u.code}</td>
                    <td className="px-4">{u.name}</td>
                    <td className="px-4 text-ink-muted">{t(`dimension.${u.dimension}`)}</td>
                    <td className="px-4 text-right">{u.decimals}</td>
                    <td className="px-4">
                      {u.is_active ? (
                        <Badge tone="success">{t("items.active")}</Badge>
                      ) : (
                        <Badge tone="neutral">{t("items.inactive")}</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {search.import ? (
          <Card aria-label={t("import.title")} className="flex min-h-0 flex-col self-start">
            <ImportPanel
              entities={["units"]}
              initial="units"
              onClose={() => void navigate({ search: {}, replace: true })}
              invalidate={[uomKeys.all]}
            />
          </Card>
        ) : (
        <Card aria-labelledby="global-conv-h" className="self-start p-5">
          <h2 id="global-conv-h" className="text-base font-semibold">
            {t("units.globalConversions")}
          </h2>
          <p className="mt-1 text-[13px] text-ink-muted">{t("units.globalConversionsHint")}</p>
          <ul className="mt-4 flex flex-col gap-1.5">
            {conversions.map((c) => (
              <li key={c.id} className="tabular rounded-xl border border-border px-3 py-2 text-[13px]">
                {t("items.conversionRule", {
                  from: c.from_uom?.code ?? "?",
                  factor: fmt.qty(c.factor),
                  to: c.to_uom?.code ?? "?",
                })}
              </li>
            ))}
          </ul>
        </Card>
        )}
      </div>
    </div>
  );
}
